import { Field as BaseField } from "@base-ui/react/field";
import { Select as BaseSelect } from "@base-ui/react/select";
import clsx from "clsx";
import type { ReactNode } from "react";
import { FieldFooter, type FieldLabelProps } from "../Field/FieldParts";
import { Icon } from "../Icon/Icon";
import fieldStyles from "../Field/Field.module.css";
import styles from "../Popup/Popup.module.css";

export interface SelectOption<V extends string = string> {
  value: V;
  label: ReactNode;
  /** Secondary line under the label (a description of the choice). */
  detail?: ReactNode;
  disabled?: boolean;
}

export interface SelectProps<V extends string = string> extends FieldLabelProps {
  options: ReadonlyArray<SelectOption<V>>;
  value?: V | null;
  defaultValue?: V | null;
  onValueChange?: (value: V | null) => void;
  /** Shown when nothing is selected ("Choose a template"). */
  placeholder?: ReactNode;
  size?: "sm" | "md";
  name?: string;
  id?: string;
  disabled?: boolean;
  readOnly?: boolean;
  required?: boolean;
  className?: string;
}

/** A labelled single-choice list (Base UI Select). Use Combobox when the list needs searching. */
export function Select<V extends string = string>({
  label,
  hideLabel,
  description,
  error,
  optional,
  options,
  value,
  defaultValue,
  onValueChange,
  placeholder = "Choose…",
  size = "md",
  name,
  id,
  disabled,
  readOnly,
  required,
  className,
}: SelectProps<V>) {
  return (
    <BaseField.Root
      name={name}
      disabled={disabled}
      invalid={error ? true : undefined}
      className={clsx(fieldStyles.field, className)}
    >
      <BaseSelect.Root<V>
        items={options}
        value={value}
        defaultValue={defaultValue}
        onValueChange={onValueChange ? (next) => onValueChange(next) : undefined}
        name={name}
        id={id}
        disabled={disabled}
        readOnly={readOnly}
        required={required}
      >
        <BaseSelect.Label className={hideLabel ? fieldStyles.hiddenLabel : fieldStyles.label}>
          {label}
          {optional && !hideLabel ? <span className={fieldStyles.optional}> (optional)</span> : null}
        </BaseSelect.Label>
        <BaseSelect.Trigger className={styles.trigger} data-size={size}>
          <BaseSelect.Value className={styles.value} placeholder={placeholder} />
          <BaseSelect.Icon className={styles.triggerIcon}>
            <Icon name="chevronsUpDown" />
          </BaseSelect.Icon>
        </BaseSelect.Trigger>
        <BaseSelect.Portal>
          <BaseSelect.Positioner className={styles.positioner} sideOffset={4} collisionPadding={8}>
            <BaseSelect.Popup className={styles.popup}>
              <BaseSelect.List className={styles.list}>
                {options.map((option) => (
                  <BaseSelect.Item
                    key={option.value}
                    value={option.value}
                    disabled={option.disabled}
                    className={styles.item}
                  >
                    <BaseSelect.ItemIndicator className={styles.itemIndicator}>
                      <Icon name="check" />
                    </BaseSelect.ItemIndicator>
                    <BaseSelect.ItemText className={styles.itemText}>{option.label}</BaseSelect.ItemText>
                    {option.detail ? <span className={styles.itemDetail}>{option.detail}</span> : null}
                  </BaseSelect.Item>
                ))}
              </BaseSelect.List>
            </BaseSelect.Popup>
          </BaseSelect.Positioner>
        </BaseSelect.Portal>
      </BaseSelect.Root>
      <FieldFooter description={description} error={error} />
    </BaseField.Root>
  );
}
